// "Report sorty's behaviour" (PIX-711 follow-up). Mirrors the backend
// SortyReport choices and caps in sorty/models.py + sorty/reports.py.

import { COMMENT_MAX_LENGTH, OTHER_REASON } from './questionFlags.js';

export const SORTY_REPORT_ENDPOINT = 'api/sorty/reports/';

export const SORTY_REPORT_SURFACE = Object.freeze({
  QUESTION_CHAT: 'question_chat',
  QUESTION_VOICE: 'question_voice',
  SORTY_AI_CHAT: 'sorty_ai_chat',
  SORTY_AI_VOICE: 'sorty_ai_voice',
  SORTY_AI_SCREEN: 'sorty_ai_screen',
});

export const SORTY_REPORT_REASONS = Object.freeze([
  { value: 'INCORRECT_OR_MISLEADING', label: 'Incorrect or misleading' },
  { value: 'DIDNT_ANSWER', label: "Didn't answer my question" },
  { value: 'INAPPROPRIATE', label: 'Inappropriate response' },
  { value: 'LAGGING_GLITCHING', label: 'Lagging or glitching' },
  { value: OTHER_REASON, label: 'Other' },
]);

export const SORTY_REPORT_COPY = Object.freeze({
  title: "Report sorty's behaviour",
  subtitle: 'Tell us what sorty got wrong so we can improve.',
});

export const SNAPSHOT_MAX_TURNS = 6;
export const SNAPSHOT_MAX_CHARS = 4000;
// The backend accepts at most 20; the snapshot window never needs more.
const MAX_INTERACTIONS = SNAPSHOT_MAX_TURNS;

/**
 * Normalise chat/voice turns to the snapshot shape: the latest turns within
 * the turn and character caps, spending the budget from the newest backwards.
 *
 * @param {{ role: string, content?: string, text?: string }[]} turns
 */
export function buildSnapshot(turns = []) {
  const normalised = turns
    .map((turn) => ({
      role: turn.role === 'user' ? 'user' : 'sorty',
      text: String(turn.text ?? turn.content ?? '').trim(),
    }))
    .filter((turn) => turn.text);
  const recent = normalised.slice(-SNAPSHOT_MAX_TURNS);
  let budget = SNAPSHOT_MAX_CHARS;
  const kept = [];
  for (let i = recent.length - 1; i >= 0 && budget > 0; i -= 1) {
    const text = recent[i].text.slice(-budget);
    kept.unshift({ role: recent[i].role, text });
    budget -= text.length;
  }
  return kept;
}

/** Question chat: something to report once sorty answered or voice began. */
export function canReportQuestionChat({ messages = [], voiceUsed = false }) {
  return voiceUsed || messages.some((m) => m.role === 'assistant' && m.content);
}

/** sorty AI chat: only once the open conversation has a sorty reply. */
export function canReportConversation(messages = []) {
  return messages.some((m) => m.role === 'MODEL' && m.content);
}

/**
 * Which surface a question-chat report is about: voice while a session is
 * open, or when voice was the last thing the student used.
 */
export function questionChatSurface({ voiceActive, lastMode }) {
  return voiceActive || lastMode === 'voice'
    ? SORTY_REPORT_SURFACE.QUESTION_VOICE
    : SORTY_REPORT_SURFACE.QUESTION_CHAT;
}

export function canSubmitSortyReport({ reason, comment }) {
  if (!SORTY_REPORT_REASONS.some((option) => option.value === reason)) {
    return false;
  }
  if (reason === OTHER_REASON) return Boolean(comment?.trim());
  return true;
}

/**
 * @param {object} args
 * @param {string} args.surface
 * @param {string} args.reason
 * @param {string} [args.comment]  only sent for Other
 * @param {object} [args.links]    { question, mock_exam_question,
 *                                   conversation, live_session, interactions }
 * @param {object[]} [args.turns]  what the student saw, newest last
 */
export function buildSortyReportPayload({
  surface,
  reason,
  comment = '',
  links = {},
  turns = [],
}) {
  const payload = {
    surface,
    reason,
    comment:
      reason === OTHER_REASON
        ? (comment ?? '').trim().slice(0, COMMENT_MAX_LENGTH)
        : '',
    snapshot: buildSnapshot(turns),
  };
  for (const key of [
    'question',
    'mock_exam_question',
    'conversation',
    'live_session',
  ]) {
    if (links[key]) payload[key] = links[key];
  }
  const interactions = [...new Set((links.interactions ?? []).filter(Boolean))];
  if (interactions.length) {
    payload.interactions = interactions.slice(-MAX_INTERACTIONS);
  }
  return payload;
}

/**
 * sorty AI thread messages ({ role: 'USER'|'MODEL', content, attachments })
 * as snapshot turns. Images can't be captured, so they're noted as "[image]".
 */
export function conversationTurns(messages = []) {
  return messages
    .filter((m) => m.role === 'USER' || m.role === 'MODEL')
    .map((m) => {
      const images = '[image] '.repeat(m.attachments?.length ?? 0);
      return {
        role: m.role === 'USER' ? 'user' : 'sorty',
        text: `${images}${m.content ?? ''}`.trim(),
      };
    });
}
